import Vue from "vue";
import * as types from "./mutation-types";

//---------------------//
//  HELPER ACTION
//---------------------//
const helperActions = {
  updateStateForChallenge({ rootState, commit }, updatedChallenge) {
    // UPDATE ALL INSTANCES OF 'UPDATED_CHALLENGE' WITHOUT RE_FETCHING EVERYTHING.
    // NOTE, CHALLENGES PAGE WILL BE RE_FETCHED via below 'setPageLoaded' action.
    // HOWEVER, BELOW ACCOUNTS FOR BEING ON THE CHALLENGE PAGE (ie, `/challenge/1`) AFTER UPDATE
    // AND GOING TO CHALLENGE EDIT AGAIN AFTER AN UPDATE. AND WHEREVER ELSE
    // THE CORRESPONDING GETTERS ARE USED (and the entire page is not reloaded).
    // ALSO, ALL_CHALLENGES IS ONLY FETCHED IF NOT FETCHED IN 'CHALLENGE_EDIT'.
    // ALSO, ADMIN_CHALLENGE_AREA (active, pending, archived) is fetched in its mounted call.
    const updatedMembersChallenges = rootState.members.challenges.map(
      challenge =>
        challenge.id === updatedChallenge.id ? updatedChallenge : challenge
    );
    commit("members/SET_CHALLENGES", updatedMembersChallenges, {
      root: true
    });
    const updatedProgramChallenges = rootState.program.challenges.map(
      challenge =>
        challenge.id === updatedChallenge.id ? updatedChallenge : challenge
    );
    commit("program/SET_CHALLENGES", updatedProgramChallenges, {
      root: true
    });
    const updatedAllChallenges = rootState.program.allChallenges.map(
      challenge =>
        challenge.id === updatedChallenge.id ? updatedChallenge : challenge
    );
    commit("program/SET_ALL_CHALLENGES", updatedAllChallenges, {
      root: true
    });
    // NOTE, THE FOLLOWING ACTIONS NEED THE MAPS RELATION,
    // the mutations handle when the updatedChallenge do not have the maps relation
    if (rootState.program.featuredChallenge.id === updatedChallenge.id) {
      commit("program/SET_FEATURED_CHALLENGE", updatedChallenge, {
        root: true
      });
    }
    if (
      Vue._.get(Vue.auth.user(), "featuredChallenge.id", null) ===
      updatedChallenge.id
    ) {
      // setting to null so that MyDashboard pulls the updated challenge instead of the cached challenge
      Vue.auth.user().featuredChallenge = null;
    }

    commit(types.SET_CHALLENGE, {
      key: "challenge" + updatedChallenge.id,
      value: updatedChallenge
    });
  }
};

const actions = {};

const challengeActions = {
  async fetchChallenge({ commit }, challengeId) {
    const challenge = await Vue.axios.get(`challenges/${challengeId}`);

    commit(types.SET_CHALLENGE, {
      key: "challenge" + challengeId,
      value: challenge.data.data
    });

    return challenge.data.data;
  },

  fetchChallengeEntities(_, payload) {
    const {
      challengeId,
      entity,
      // sortColumn,
      // sortOrder,
      page,
      perPage = 7
    } = payload;

    const params = { page, perPage };

    return Vue.axios
      .get(`challenges/${challengeId}/${entity}`, { params })
      .then(response => {
        return response.data;
      });
  },

  async updateChallenge({ dispatch }, payload) {
    const { challengeId, formData } = payload;

    return Vue.axios
      .post(`challenges/${challengeId}`, formData)
      .then(response => {
        if (response && response.data.success) {
          const updatedChallenge = response.data.data;

          dispatch("updateStateForChallenge", updatedChallenge);

          dispatch(
            "pages/setPageLoaded",
            { key: "Challenges", value: false },
            {
              root: true
            }
          );

          dispatch(
            "snackBar",
            {
              duration: 10000,
              queue: false,
              message: "Challenge successfully updated!",
              type: "is-success"
            },
            { root: true }
          );
        }
        // refresh the $auth.user() cached Challenges
        Vue.auth.fetch();
        return response;
      })
      .catch(error => {
        dispatch(
          "snackBar",
          {
            duration: 10000,
            queue: false,
            message: Vue._.get(
              error.response,
              "data.error.message",
              "There was an error."
            ),
            type: "is-danger"
          },
          { root: true }
        );

        throw error.response;
      });
  },

  join({ commit, dispatch }, payload) {
    const { challenge, memberId, team, inviteCode } = payload;
    let challengeJoining = Vue._.cloneDeep(challenge); // objects are passed by reference so this is needed or you will be mutating the state

    commit(types.SET_LOADED_STATUS, {
      key: "joining",
      value: true
    });

    const data = {};
    let entity = "member";
    if (memberId) {
      Object.assign(data, { memberIds: [memberId] });
    }
    if (team) {
      Object.assign(data, { teamIds: [team.id] });
      entity = "team";
    }
    if (inviteCode) {
      Object.assign(data, { invite: inviteCode });
      if (challenge.between === "groups") {
        entity = "team";
      }
    }

    return Vue.axios
      .post(`challenges/${challengeJoining.id}/add-${entity}`, data)
      .then(async response => {
        if (response && response.data.success) {
          let isTeam = entity === "team";

          if (isTeam) {
            challengeJoining.teams.push(team);
            challengeJoining.teamCount++;
            dispatch("updateStateForChallenge", challengeJoining);
          }
          // Note: 'ChallengePage.vue' depends on this mutation for reloading the page
          commit("members/ADD_CHALLENGE", challengeJoining, { root: true });
          // refresh auth.user().challenges
          await Vue.auth.fetch();

          commit(types.SET_LOADED_STATUS, {
            key: "joining",
            value: false
          });

          dispatch(
            "snackBar",
            {
              message: `Successfully ${
                isTeam ? "added to" : "joined"
              } challenge ${challengeJoining.name}!`
            },
            { root: true }
          );
        }
      })
      .catch(error => {
        dispatch(
          "snackBar",
          {
            duration: 10000,
            queue: false,
            message: error.response.data.error.message,
            type: "is-danger"
          },
          { root: true }
        );

        // after the error is displayed, wait a bit then refresh the challenge page
        setTimeout(() => {
          Vue.router.push(
            { name: "Challenge", params: { challengeId: challengeJoining.id } },
            () => {
              Vue.router.go();
            }
          );
        }, 5000);

        throw error;
      });
  },

  request({ dispatch }, payload) {
    const { challengeId, memberId } = payload;

    return Vue.axios
      .post(`challenges/${challengeId}/request-join`, {
        memberId
      })
      .catch(error => {
        dispatch(
          "snackBar",
          {
            duration: 10000,
            queue: false,
            message: error.response.data.error.message,
            type: "is-danger"
          },
          { root: true }
        );
      });
  },

  async fetchLeaderboard({ commit }, payload) {
    let {
      challengeId,
      page = 1,
      perPage = 5,
      organizationLeaderboard = 0
    } = payload;

    if (challengeId) {
      commit(types.SET_LOADED_STATUS, {
        key: "leaderboardLoaded",
        value: false
      });

      const leaderboard = await Vue.axios.get(
        `challenges/${challengeId}/leaderboard`,
        {
          params: { perPage, page, organizationLeaderboard }
        }
      );
      if (organizationLeaderboard) {
        commit(types.SET_CHALLENGE_LEADERBOARD, {
          key: "organizationLeaderboard" + challengeId,
          value: leaderboard.data
        });
      } else {
        commit(types.SET_CHALLENGE_LEADERBOARD, {
          key: "leaderboard" + challengeId,
          value: leaderboard.data
        });
      }

      commit(types.SET_LOADED_STATUS, {
        key: "leaderboardLoaded",
        value: true
      });

      return leaderboard.data;
    }
  },

  async fetchChallengeAggregate({ commit }, challengeId) {
    commit(types.SET_LOADED_STATUS, {
      key: "aggregateLoaded",
      value: false
    });

    const challengeData = await Vue.axios.get(
      `challenges/${challengeId}/aggregate-data`
    );

    commit(types.SET_CHALLENGE_AGGREGATE, {
      key: "aggregate" + challengeId,
      value: challengeData.data.data
    });
    commit(types.SET_LOADED_STATUS, {
      key: "aggregateLoaded",
      value: true
    });

    return challengeData;
  },

  // TODO remove me - not needed / not being used
  setInvitedAllMembers({ commit }, bool) {
    // because inviting all members is done via a queue and not immediate,
    // were setting this piece of state to make the user know the invites were sent.
    commit(types.SET_ALL_MEMBERS_INVITED, bool);
  },

  addEntitiesToChallenge({ commit, dispatch }, payload) {
    let { challenge, entityIds = [], includeAll = false } = payload;
    challenge = Vue._.cloneDeep(challenge); // objects are passed by reference so this is needed or you will be mutating the state

    let entity = challenge.between === "groups" ? "team" : "member";

    let data = {
      [entity + "Ids"]: entityIds
    };

    if (includeAll) {
      data = { includeAll };
    }

    return Vue.axios
      .post(`challenges/${challenge.id}/add-${entity}`, data)
      .then(response => {
        if (response && response.data.success) {
          dispatch(
            "pages/setPageLoaded",
            { key: "Challenges", value: false },
            {
              root: true
            }
          );
          dispatch(
            "pages/setPageLoaded",
            { key: `Challenge${challenge.id}`, value: false },
            {
              root: true
            }
          );

          dispatch(
            "snackBar",
            {
              message: `Successfully added ${entity}s to challenge ${challenge.name}!`
            },
            { root: true }
          );
        }
        // flag to refresh auth.user() user next time to reflect join status next time user goes to challenge page
        commit(types.SET_REFRESH_JOINED_STATUS_NEEDED, true);
        return response;
      })
      .catch(error => {
        const errorMessage = Vue._.get(
          error,
          "response.data.error.message",
          "Shoot, there was an error. Please try again, or contact wellness@be-engaged.co"
        );
        dispatch(
          "snackBar",
          {
            duration: 10000,
            queue: false,
            message: errorMessage,
            type: "is-danger"
          },
          { root: true }
        );

        // after the error is displayed, wait a bit then refresh the challenge page
        setTimeout(() => {
          Vue.router.push(
            { name: "Challenge", params: { challengeId: challenge.id } },
            () => {
              Vue.router.go();
            }
          );
        }, 5000);
      });
  },

  removeMembersFromChallenge({ commit, dispatch }, payload) {
    let { challengeId, memberIds } = payload;

    let formData = new FormData();
    memberIds.map((memberId, index) => {
      formData.set(`memberIds[${index}]`, memberId);
    });

    return Vue.axios
      .post(`challenges/${challengeId}/remove-member`, formData)
      .then(response => {
        commit(types.SET_REFRESH_JOINED_STATUS_NEEDED, true);
        return response;
      })
      .catch(error => {
        dispatch(
          "snackBar",
          {
            duration: 10000,
            queue: false,
            message: error.response.data.error.message,
            type: "is-danger"
          },
          { root: true }
        );

        throw error.response;
      });
  },

  removeTeamsFromChallenge({ dispatch }, payload) {
    let { challengeId, teamIds } = payload;

    let formData = new FormData();
    teamIds.map((teamId, index) => {
      formData.set(`teamIds[${index}]`, teamId);
    });

    return Vue.axios
      .post(`challenges/${challengeId}/remove-team`, formData)
      .then(response => response)
      .catch(error => {
        dispatch(
          "snackBar",
          {
            duration: 10000,
            queue: false,
            message: error.response.data.error.message,
            type: "is-danger"
          },
          { root: true }
        );

        return false;
      });
  },

  sentChallengeInvites({ commit, dispatch }, challengeId) {
    return Vue.axios
      .get(`challenges/${challengeId}/invites`)
      .then(response => {
        commit(types.SENT_CHALLENGE_INVITES, response.data.data);
        return response;
      })
      .catch(error => {
        dispatch(
          "snackBar",
          {
            duration: 10000,
            queue: false,
            message: error.response.data.error.message,
            type: "is-danger"
          },
          { root: true }
        );

        return false;
      });
  },

  challengeStatsDownload(_, challengeId) {
    return Vue.axios
      .get(`challenges/${challengeId}/stats-download`, {
        responseType: "arraybuffer"
      })
      .catch(error => {
        return error.response;
      });
  },

  completeChallengeStatsDownload(_, payload) {
    const { programId, startDate, endDate } = payload;
    return Vue.axios
      .get(
        `programs/${programId}/challenge-completion-summary-download?startDate=${startDate}&endDate=${endDate}`,
        {
          responseType: "arraybuffer"
        }
      )
      .catch(error => {
        if (error.response && error.response.data) {
          try {
            // Convert ArrayBuffer to a string
            const errorText = new TextDecoder("utf-8").decode(
              error.response.data
            );
            // Parse the string into a JSON object
            const errorJson = JSON.parse(errorText);
            return { data: errorJson };
          } catch (parseError) {
            return error.response;
          }
        } else {
          return error.response;
        }
      });
  },

  teamChallengeWinnersDownload(_, challengeId) {
    return Vue.axios
      .get(`challenges/${challengeId}/team-winners-report`, {
        responseType: "arraybuffer"
      })
      .catch(error => {
        return error.response;
      });
  },

  deleteChallenge({ commit, dispatch, rootState }, challengeId) {
    return Vue.axios
      .delete(`challenges/${challengeId}`)
      .then(response => {
        // update state for deleted challenge ... todo programChallenge and myChallenges need to remove deleted challenge
        commit(types.DELETE_CHALLENGE, {
          key: "challenge" + challengeId
        });
        if (
          Vue._.get(Vue.auth.user(), "featuredChallenge.id", null) ===
          challengeId
        ) {
          Vue.auth.user().featuredChallenge = null;
        }
        if (
          Vue._.get(rootState, "program.featuredChallenge.id", null) ===
          challengeId
        ) {
          commit("program/SET_FEATURED_CHALLENGE", null, {
            root: true
          });
        }
        // refresh the $auth.user() cached Challenges
        Vue.auth.fetch();

        dispatch(
          "snackBar",
          {
            duration: 10000,
            queue: false,
            message: "Challenge successfully deleted!",
            type: "is-success"
          },
          { root: true }
        );

        return response;
      })
      .catch(error => {
        dispatch(
          "snackBar",
          {
            message: error.response.data.error.message,
            type: "is-danger",
            queue: false,
            duration: 10000
          },
          { root: true }
        );
      });
  }
};

const questionActions = {
  async questionReportDownload(_, { questionId, programId }) {
    let response = null;
    try {
      response = await Vue.axios.post(
        `questions/${programId}/stats-download`,
        {
          questionList: questionId
        },
        { responseType: "arraybuffer" }
      );
    } catch (error) {
      response = error.response;
    }
    return response;
  }
};

const discussionActions = {
  async fetchChallengeDiscussion({ commit }, payload) {
    let { challengeId, photosOnly, page } = payload;

    photosOnly = photosOnly ? `&photosOnly=1` : "";
    page = page ? `&page=${page}` : "";

    commit(types.SET_LOADED_STATUS, { key: "discussionLoaded", value: false });

    const discussion = await Vue.axios.get(
      `challenges/${challengeId}/discussion?${photosOnly}${page}`
    );

    commit(types.SET_CHALLENGE_DISCUSSION, {
      key: "discussion" + challengeId,
      value: discussion.data
    });
    commit(types.SET_LOADED_STATUS, { key: "discussionLoaded", value: true });
  },

  async updateDiscussion(_, payload) {
    const { itemId, formData } = payload;

    let response = null;

    try {
      response = await Vue.axios.post(`discussions/${itemId}`, formData);
    } catch (error) {
      response = error.response.data;
    }

    return response;
  },

  async storeDiscussion(_, payload) {
    const { formData } = payload;

    let response = null;

    try {
      response = await Vue.axios.post(`discussions`, formData);
    } catch (error) {
      response = error.response.data;
    }

    return response;
  },

  async deleteChallengeDiscussionPost({ dispatch }, discussionPostId) {
    return Vue.axios
      .delete(`discussions/${discussionPostId}`)
      .then(response => {
        dispatch(
          "snackBar",
          {
            type: "is-success",
            message: "The discussion post has been deleted."
          },
          { root: true }
        );

        return response;
      })
      .catch(error => {
        dispatch(
          "snackBar",
          {
            message: error.response.data.error.message,
            type: "is-danger",
            queue: false,
            duration: 10000
          },
          { root: true }
        );
      });
  },

  async deleteDiscussionPhoto(_, payload) {
    const { itemId } = payload;

    let response = null;

    try {
      response = await Vue.axios.post(`discussions/${itemId}/destroy-image`);
    } catch (error) {
      response = error.response.data;
    }

    return response;
  },

  fetchSubscriptions(_, topicId) {
    return Vue.axios
      .get(`topics/${topicId}/subscriptions`)
      .then(response => response.data.data.memberIds);
  },

  subscribeAction({ dispatch }, payload) {
    const { topicId, subscribe } = payload;

    return Vue.axios
      .post(`topics/${topicId}/subscription`, {
        subscribe
      })
      .catch(error => {
        dispatch(
          "snackBar",
          {
            message: error.response.data.error.message,
            type: "is-danger",
            queue: false,
            duration: 10000
          },
          { root: true }
        );
      });
  },

  setRefreshStatusNeeded({ commit }, bool) {
    commit(types.SET_REFRESH_JOINED_STATUS_NEEDED, bool);
  }
};

//  MOBILE ACTIONS
//---------------------//
const mobileActions = {
  async fetchMobileChallenge({ commit }, payload) {
    let { mobileToken, challengeId } = payload;
    let params = {
      mobileToken: mobileToken
    };
    const challenge = await Vue.axios.get(`mobile/challenge/${challengeId}`, {
      params
    });

    commit(types.SET_MOBILE_CHALLENGE, challenge.data.data);
    commit(types.SET_LOADED_STATUS, {
      key: "mobileChallengeLoaded",
      value: true
    });
  },
  mobileJoin(_, payload) {
    const { challenge, mobileToken } = payload;
    let challengeJoining = Vue._.cloneDeep(challenge); // objects are passed by reference so this is needed or you will be mutating the state
    const data = {
      mobileToken: mobileToken
    };

    return Vue.axios
      .post(`mobile/challenges/${challengeJoining.id}/add-member`, data)
      .then(async response => {
        if (response && response.data.success) {
          return response;
        }
      })
      .catch(error => {
        throw error.response;
      });
  },

  mobileRemoveMemberFromChallenge(_, payload) {
    let { challengeId, mobileToken } = payload;

    let data = {
      mobileToken
    };

    return Vue.axios
      .post(`mobile/challenges/${challengeId}/remove-member`, data)
      .then(response => {
        return response;
      })
      .catch(error => {
        throw error.response;
      });
  },
  updateStateForMobileChallenge({ commit }, updatedChallenge) {
    commit(types.SET_MOBILE_CHALLENGE, updatedChallenge);
  }
};

Object.assign(
  actions,
  helperActions,
  challengeActions,
  questionActions,
  discussionActions,
  mobileActions
);

export default actions;
